// src/hooks/useTasklyWS.js
import { useEffect, useRef } from "react";

export default function useTasklyWS({ token, onEvent, enabled = true }) {
  const ref = useRef({ ws: null, queue: [], retryDelay: 0, closing: false });
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent; // always latest handler

  const closeSocket = () => {
    const { ws } = ref.current;
    if (
      ws &&
      (ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING)
    ) {
      try {
        ws.close();
      } catch {}
    }
    ref.current.ws = null;
  };

  const scheduleReconnect = () => {
    if (ref.current.closing || !enabled) return;
    ref.current.retryDelay = Math.min(
      ref.current.retryDelay ? ref.current.retryDelay * 2 : 500,
      10_000
    );
    setTimeout(ensureConnected, ref.current.retryDelay);
  };

  const ensureConnected = () => {
    if (!enabled || !token) return false;

    const { ws } = ref.current;
    if (ws && ws.readyState === WebSocket.OPEN) return true;
    if (ws && ws.readyState === WebSocket.CONNECTING) return false;

    const url =
      "wss://v0ostq7z6j.execute-api.us-west-2.amazonaws.com/prod" +
      "?auth=" +
      encodeURIComponent(token || "");

    const sock = new WebSocket(url);
    ref.current.ws = sock;

    sock.onopen = () => {
      ref.current.retryDelay = 0;
      ref.current.queue.forEach((d) => sock.send(d));
      ref.current.queue.length = 0;
    };

    sock.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data);
        onEventRef.current?.(evt); // ← always latest
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    sock.onclose = scheduleReconnect;
    sock.onerror = () => sock.close();
    return false;
  };

  const send = (obj) => {
    const data = JSON.stringify(obj);
    if (ensureConnected()) ref.current.ws.send(data);
    else ref.current.queue.push(data);
  };

  useEffect(() => {
    if (!enabled || !token) {
      closeSocket();
      return;
    }
    ref.current.closing = false;
    ensureConnected();

    return () => {
      ref.current.closing = true;
      closeSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token]);

  const rs = ref.current.ws?.readyState;
  return {
    send,
    socket: ref.current.ws,
    connected: rs === WebSocket.OPEN,
    connecting: rs === WebSocket.CONNECTING,
  };
}
